import Hls from 'hls.js';

/** 통합관제 UI·메모리 한도 (초). 이 범위를 벗어나면 클램프한다. */
export const HLS_BUFFER_SEC_MIN = 6;
export const HLS_BUFFER_SEC_MAX = 15;
export const HLS_BUFFER_SEC_DEFAULT = 10;

export function clampHlsBufferSec(sec: number): number {
  if (!Number.isFinite(sec) || sec <= 0) return HLS_BUFFER_SEC_DEFAULT;
  // 예전 기본 60(초로 오용)·최적화 MB 값 등 비정상
  if (sec > 30) return HLS_BUFFER_SEC_DEFAULT;
  return Math.min(HLS_BUFFER_SEC_MAX, Math.max(HLS_BUFFER_SEC_MIN, Math.round(sec)));
}

/** iOS/Android WebView·Safari에서 HLS worker 이슈 완화 */
export function preferHlsWorker(): boolean {
  if (typeof navigator === 'undefined') return true;
  return !/iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

/** HLS startLoad/loadSource 직후 play()가 끊길 때 나는 브라우저 경고(치명 아님) */
export function isPlayInterruptedError(err: unknown): boolean {
  if (!err) return false;
  const name = (err as { name?: string }).name;
  if (name === 'AbortError') return true;
  const msg = String((err as Error).message ?? err);
  return /interrupted|new load request/i.test(msg);
}

export function tryPlayMutedInline(video: HTMLVideoElement): Promise<void> {
  video.muted = true;
  video.playsInline = true;
  return video.play().catch((err) => {
    if (isPlayInterruptedError(err)) return;
    throw err;
  });
}

export type MonitoringHlsOptions = {
  /** 앞으로 미리 받을 영상 길이(초). 클수록 끊김↓·RAM/네트워크↑ */
  bufferLengthSec?: number;
};

/**
 * 모니터링 팝업·통합관제용 Hls.js.
 * 현장 게이트웨이가 1초 조각을 중앙에 올리는 경로라 초저지연(LL-HLS)이 아님.
 * lowLatencyMode 를 켜면 조각이 잠깐 밀릴 때 재생→정지 반복이 잘 난다.
 */
export function createMonitoringPopupStyleHls(options?: MonitoringHlsOptions): Hls {
  const bufferSec = clampHlsBufferSec(
    options?.bufferLengthSec ?? HLS_BUFFER_SEC_DEFAULT
  );
  // 뒤쪽(이미 본) 버퍼는 짧게 — 멀티캠에서 SourceBuffer 비대화 방지
  const backSec = Math.min(30, Math.max(12, bufferSec * 2));
  // 1920·재인코딩(~0.25MB/s) ~ 원본 복사(~1MB/s) 여유
  const maxBufferSizeBytes = Math.max(8, bufferSec) * 2_500_000;

  return new Hls({
    enableWorker: preferHlsWorker(),
    lowLatencyMode: false,
    liveSyncDurationCount: 3,
    liveMaxLatencyDurationCount: Math.max(6, Math.ceil(bufferSec / 2) + 2),
    backBufferLength: backSec,
    maxBufferSize: maxBufferSizeBytes,
    maxBufferLength: bufferSec,
    maxMaxBufferLength: bufferSec,
    fragLoadingMaxRetry: 8,
    manifestLoadingMaxRetry: 8,
    levelLoadingMaxRetry: 8,
    fragLoadingRetryDelay: 400,
    manifestLoadingRetryDelay: 400,
    levelLoadingRetryDelay: 400,
    startLevel: -1,
    maxBufferHole: 1.5,
    nudgeMaxRetry: 5,
    autoStartLoad: true,
  });
}

/** 팝업 setInterval(5s): paused/ended 시 재생 재시도 */
export const HLS_PAUSED_RESUME_INTERVAL_MS = 5000;

/**
 * VOD/녹화 HLS가 끝났을 때 전체 destroy 없이 처음부터 재생(마지막 프레임 유지).
 * @returns true면 soft replay 성공(또는 시도), false면 호출 측에서 hard re-init 필요
 */
export function replayMonitoringStreamSoft(
  hls: Hls | null,
  video: HTMLVideoElement,
  url: string
): boolean {
  const trimmed = url.trim();
  if (hls) {
    try {
      hls.stopLoad();
      hls.startLoad(0);
      if (Number.isFinite(video.duration) && video.duration > 0) {
        video.currentTime = 0;
      }
      void tryPlayMutedInline(video);
      return true;
    } catch {
      return false;
    }
  }
  if (trimmed && !trimmed.includes('.m3u8')) {
    try {
      video.loop = true;
      video.currentTime = 0;
      void tryPlayMutedInline(video);
      return true;
    } catch {
      return false;
    }
  }
  if (video.canPlayType('application/vnd.apple.mpegurl') && trimmed.includes('.m3u8')) {
    try {
      video.currentTime = 0;
      void tryPlayMutedInline(video);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}
